import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import WizardProgress from './WizardProgress';
import StepIncome from './StepIncome';
import StepFixedExpenses from './StepFixedExpenses';
import StepVariableExpenses from './StepVariableExpenses';
import StepGoals from './StepGoals';
import { useBudget } from '../../context/BudgetContext';
import { setWizardStep, completeWizard } from '../../context/budgetActions';

const STEPS = [StepIncome, StepFixedExpenses, StepVariableExpenses, StepGoals];

export default function SetupWizard() {
  const { t } = useTranslation();
  const { state, dispatch } = useBudget();
  const step = state.wizardStep;
  const StepComponent = STEPS[step];
  const [animating, setAnimating] = useState(false);
  const prevStep = useRef(step);

  // Trigger fade animation on step change
  useEffect(() => {
    if (prevStep.current !== step) {
      setAnimating(true);
      const timer = setTimeout(() => setAnimating(false), 50);
      prevStep.current = step;
      return () => clearTimeout(timer);
    }
  }, [step]);

  const canProceed = () => {
    switch (step) {
      case 0:
        return state.incomeSources.length > 0;
      case 1:
        return true;
      case 2:
        return true;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      dispatch(setWizardStep(step + 1));
    } else {
      dispatch(completeWizard());
    }
  };

  const handleBack = () => {
    if (step > 0) dispatch(setWizardStep(step - 1));
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <WizardProgress activeStep={step} />

      <Box
        sx={{
          transition: 'opacity 0.3s ease, transform 0.3s ease',
          opacity: animating ? 0 : 1,
          transform: animating ? 'translateY(8px)' : 'translateY(0)',
        }}
      >
        <StepComponent />
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          disabled={step === 0}
          sx={{ borderColor: '#1E3A5F', color: '#1E3A5F', minHeight: 44 }}
        >
          {t('budgetPlanner.wizard.back', 'Atrás')}
        </Button>

        {/* Auto-save note */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
          <CloudDoneOutlinedIcon sx={{ fontSize: 14, color: '#5F6B7A' }} />
          <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
            {t('budgetPlanner.wizard.autoSaveNote', 'Tus datos se guardan automáticamente')}
          </Typography>
        </Box>

        <Button
          variant="contained"
          endIcon={step === STEPS.length - 1 ? <CheckIcon /> : <ArrowForwardIcon />}
          onClick={handleNext}
          disabled={!canProceed()}
          sx={{ bgcolor: '#1E3A5F', '&:hover': { bgcolor: '#1A3152' }, minHeight: 44 }}
        >
          {step === STEPS.length - 1
            ? t('budgetPlanner.wizard.finish', 'Ver mi presupuesto')
            : t('budgetPlanner.wizard.next', 'Siguiente')}
        </Button>
      </Stack>
    </Box>
  );
}
